import { replaceSourceNameForTenantIfRequired } from '@modules/common/utils';
import { IntegrationCardDetailsText } from '@modules/integrationsV2/components/IntegrationCardDetailsText';
import { DisabledIntegrationIndicator } from '@modules/integrationsV2/components/IntegrationStateIndicator';
import { useIsIntegrationContextEnabled } from '@modules/integrationsV2/hooks';
import { IntegrationSource } from '@modules/integrationsV2/types';
import { Stack, Typography } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationViewFragment,
} from 'generated/graphql';
import { capitalize } from 'lodash';

interface IntegrationSourceTitleProps {
  integrationSource: IntegrationSource;
  integrations: IntegrationViewFragment[];
}

export const IntegrationSourceTitle = ({
  integrationSource,
  integrations,
}: IntegrationSourceTitleProps) => {
  const isIntegrationContextEnabled = useIsIntegrationContextEnabled();

  const {
    integrationType,
    channel: { displayName, type: channelType },
    namespace,
    isSingleConnectionSource,
    description,
  } = integrationSource;

  const currentIntegration = isSingleConnectionSource
    ? integrations.find(
        (integration) => integration.id === integrationSource.integrationID,
      )
    : null;

  const isFileUploadIntegration =
    channelType === IntegrationChannelType.FileUpload;

  return (
    <Stack rowGap={0.5}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography variant="h6">{displayName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {capitalize(integrationType)}{' '}
          {isSingleConnectionSource ? 'Integration' : 'Integrations'}
        </Typography>
        {integrationSource.isDisabled && (
          <DisabledIntegrationIndicator variant="chip" />
        )}
      </Stack>
      {namespace && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {replaceSourceNameForTenantIfRequired(namespace)}
        </Typography>
      )}
      {currentIntegration?.externalID && !isFileUploadIntegration && (
        <IntegrationCardDetailsText
          text={currentIntegration.externalID}
          variant="caption"
          color="text.secondary"
          maxWidth="400px"
          showClickToCopyTooltip
        />
      )}
      {description && isIntegrationContextEnabled && (
        <Typography variant="body2" color="text.secondary">
          Description: {description}
        </Typography>
      )}
    </Stack>
  );
};
